/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Crop, Trash2, Check, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/apiClient";
import { fileUrl, LAYOUTS, uploadFile, errMsg } from "../../lib/reportApi";
import { fmtDateTimeIST } from "../../lib/formatDateTime";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import PhotoCropDialog from "./PhotoCropDialog";

function PhotoArea({ item, canEdit, onFile, onCrop, onRemove, busy }) {
  const pickRef = useRef(null);
  const camRef = useRef(null);
  const [over, setOver] = useState(false);
  const take = (e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; };
  const drop = (e) => {
    e.preventDefault(); setOver(false);
    const f = Array.from(e.dataTransfer?.files || []).find((x) => x.type.startsWith("image/") || /\.(heic|heif)$/i.test(x.name));
    if (f && canEdit) onFile(f);
  };
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}
      className={`border-2 border-dashed p-3 transition-colors ${over ? "border-readyGreen bg-readyGreen/5" : "border-cmdBorder"}`}
      data-testid="report-item-photo-area">
      <input ref={pickRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={take} data-testid="report-item-photo-input" />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={take} data-testid="report-item-camera-input" />
      {item.photo ? (
        <img src={fileUrl(item.photo)} alt="" className="w-full max-h-72 object-contain bg-zinc-100" data-testid="report-item-photo-preview" />
      ) : (
        <div className="h-40 flex flex-col items-center justify-center text-zinc-500 text-sm gap-1">
          <ImagePlus className="w-6 h-6" />
          <span>Drag & drop a photo here</span>
          <span className="text-xs">JPG, PNG, WEBP, HEIC</span>
        </div>
      )}
      {busy && <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</div>}
      {canEdit && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button type="button" onClick={() => pickRef.current?.click()} className="border border-cmdBorder px-2 py-1 text-[11px] uppercase tracking-widest flex items-center gap-1" data-testid="report-item-photo-pick">
            <ImagePlus className="w-3 h-3" /> {item.photo ? "Replace" : "Upload"}
          </button>
          <button type="button" onClick={() => camRef.current?.click()} className="border border-cmdBorder px-2 py-1 text-[11px] uppercase tracking-widest flex items-center gap-1" data-testid="report-item-photo-camera">
            <Camera className="w-3 h-3" /> Camera
          </button>
          {item.photo && (
            <>
              <button type="button" onClick={onCrop} className="border border-cmdBorder px-2 py-1 text-[11px] uppercase tracking-widest flex items-center gap-1" data-testid="report-item-photo-crop">
                <Crop className="w-3 h-3" /> Crop / Rotate
              </button>
              <button type="button" onClick={onRemove} className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-[11px] uppercase tracking-widest flex items-center gap-1" data-testid="report-item-photo-remove">
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReportItemEditor({ item, open, canEdit, onClose, onChanged }) {
  const [form, setForm] = useState({ heading: "", description: "", layout: 1 });
  const [saveState, setSaveState] = useState("saved");
  const [busy, setBusy] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const timer = useRef(null);
  const latest = useRef(item);

  useEffect(() => {
    if (item) {
      latest.current = item;
      setForm({ heading: item.heading || "", description: item.description || "", layout: item.layout || 1 });
      setSaveState("saved");
    }
  }, [item?.id]);

  const persist = async (next) => {
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/report-items/${latest.current.id}`, next);
      latest.current = data;
      onChanged(data);
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      toast.error(errMsg(e, "Auto-save failed"));
    }
  };

  const change = (patch, immediate = false) => {
    const next = { ...form, ...patch };
    setForm(next);
    setSaveState("pending");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(next), immediate ? 0 : 700);
  };

  const flushAndClose = async () => {
    if (saveState === "pending") { clearTimeout(timer.current); await persist(form); }
    onClose();
  };

  const upload = async (file) => {
    setBusy(true);
    try {
      const { data } = await uploadFile(`/report-items/${latest.current.id}/photo`, file);
      latest.current = data;
      onChanged(data);
    } catch (e) {
      toast.error(errMsg(e, "Photo upload failed"));
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async () => {
    const { data } = await api.delete(`/report-items/${latest.current.id}/photo`);
    latest.current = data;
    onChanged(data);
  };

  if (!item) return null;
  const cur = latest.current || item;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && flushAndClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="report-item-editor">
        <DialogHeader>
          <DialogTitle className="font-head uppercase tracking-tight text-2xl flex items-center gap-3">
            Report Item
            <span className="text-[10px] font-mono-live tracking-widest text-zinc-500 normal-case" data-testid="report-item-save-state">
              {saveState === "saving" || saveState === "pending" ? "saving…" : saveState === "error" ? "not saved" : <span className="inline-flex items-center gap-1 text-readyGreen"><Check className="w-3 h-3" /> saved</span>}
            </span>
          </DialogTitle>
        </DialogHeader>
        <label className="mc-label">Heading</label>
        <input value={form.heading} disabled={!canEdit} onChange={(e) => change({ heading: e.target.value })} maxLength={140}
          placeholder="e.g. Turnstiles live at all 9 gates" className="border border-cmdBorder p-2 text-sm w-full" data-testid="report-item-heading" />
        <PhotoArea item={cur} canEdit={canEdit} busy={busy} onFile={upload} onCrop={() => setCropOpen(true)} onRemove={removePhoto} />
        <label className="mc-label">Comments / Description <span className="normal-case tracking-normal text-zinc-400">(appears below the photo)</span></label>
        <textarea value={form.description} disabled={!canEdit} onChange={(e) => change({ description: e.target.value })} rows={4}
          className="border border-cmdBorder p-2 text-sm w-full" data-testid="report-item-description" />
        <label className="mc-label">Page layout</label>
        <div className="grid grid-cols-4 gap-2" data-testid="report-item-layout">
          {LAYOUTS.map((l) => (
            <button key={l.value} type="button" disabled={!canEdit} onClick={() => change({ layout: l.value }, true)} data-testid={`report-item-layout-${l.value}`}
              className={`border py-2 text-[11px] uppercase tracking-widest ${form.layout === l.value ? "border-black bg-black text-white" : "border-cmdBorder"}`}>
              {l.label}
            </button>
          ))}
        </div>
        {!!cur.history?.length && (
          <details className="mt-2 text-xs" data-testid="report-item-history">
            <summary className="mc-label cursor-pointer flex items-center gap-1"><History className="w-3 h-3" /> Audit trail ({cur.history.length})</summary>
            <ul className="mt-2 space-y-1">
              {cur.history.slice().reverse().map((h, i) => (
                <li key={i} className="text-zinc-500"><span className="text-zinc-800">{h.by}</span> {h.action} · {fmtDateTimeIST(h.at)}</li>
              ))}
            </ul>
          </details>
        )}
        <div className="flex justify-end mt-2 sticky bottom-0 bg-white py-2 -mb-2 border-t border-cmdBorder">
          <button type="button" onClick={flushAndClose} className="bg-black text-white px-4 py-2 text-xs font-bold uppercase tracking-widest" data-testid="report-item-done">Done</button>
        </div>
        {cropOpen && (
          <PhotoCropDialog src={fileUrl(cur.photo)} open={cropOpen} onClose={() => setCropOpen(false)}
            onSave={async (f) => { setCropOpen(false); await upload(f); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}
